import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Wallet, DollarSign, ArrowUpCircle, ArrowDownCircle, 
  Clock, CheckCircle2, XCircle, AlertCircle, 
  Plus, Minus, History, Calculator, RefreshCw
} from 'lucide-react';
import api from '@/services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

// Format date/time
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export function CashPage() {
  const [loading, setLoading] = useState(true);
  const [currentSession, setCurrentSession] = useState(null);
  const [hasOpenSession, setHasOpenSession] = useState(false);
  const [movements, setMovements] = useState([]);
  const [history, setHistory] = useState([]);
  
  // Dialog states
  const [openCashDialog, setOpenCashDialog] = useState(false);
  const [closeCashDialog, setCloseCashDialog] = useState(false);
  const [movementDialog, setMovementDialog] = useState(false);
  
  // Form states
  const [openingAmount, setOpeningAmount] = useState('200.00');
  const [cashRegisterId, setCashRegisterId] = useState('CAIXA01');
  const [closingAmount, setClosingAmount] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [movementType, setMovementType] = useState('SANGRIA');
  const [movementAmount, setMovementAmount] = useState('');
  const [movementNotes, setMovementNotes] = useState('');

  // Load current session
  const loadCurrentSession = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/api/cash/session/current`);
      setCurrentSession(response.data.session);
      setHasOpenSession(response.data.has_open_session);
      
      if (response.data.has_open_session && response.data.session) {
        setClosingAmount(response.data.session.current_balance?.toString() || '');
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Erro ao carregar sessão do caixa');
    }
  }, []);

  // Load movements
  const loadMovements = useCallback(async () => {
    if (!hasOpenSession) return;
    try {
      const response = await api.get(`${API_URL}/api/cash/movements`);
      setMovements(response.data.movements || []);
    } catch (error) {
      // Session might not be open
      setMovements([]);
    }
  }, [hasOpenSession]);

  // Load history
  const loadHistory = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/api/cash/session/history?limit=10`);
      setHistory(response.data.sessions || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await loadCurrentSession();
      await loadHistory();
      setLoading(false);
    };
    loadData();
  }, [loadCurrentSession, loadHistory]);

  // Load movements when session changes
  useEffect(() => {
    if (hasOpenSession) {
      loadMovements();
    }
  }, [hasOpenSession, loadMovements]);

  // Open cash register
  const handleOpenCash = async () => {
    try {
      const amount = parseFloat(openingAmount.replace(',', '.'));
      if (isNaN(amount) || amount < 0) {
        toast.error('Valor de abertura inválido');
        return;
      }
      
      await api.post(`${API_URL}/api/cash/session/open`, {
        cash_register_id: cashRegisterId,
        opening_amount: amount
      });
      
      toast.success('Caixa aberto com sucesso!');
      setOpenCashDialog(false);
      await loadCurrentSession();
      await loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao abrir caixa');
    }
  };

  // Close cash register
  const handleCloseCash = async () => {
    try {
      const amount = parseFloat(closingAmount.replace(',', '.'));
      if (isNaN(amount) || amount < 0) {
        toast.error('Valor de fechamento inválido');
        return;
      }
      
      const response = await api.post(
        `${API_URL}/api/cash/session/close?closing_amount=${amount}${closingNotes ? `&notes=${encodeURIComponent(closingNotes)}` : ''}`
      );
      
      const result = response.data;
      
      if (result.status === 'OK') {
        toast.success('Caixa fechado com sucesso! Conferência OK.');
      } else if (result.status === 'SOBRA') {
        toast.warning(`Caixa fechado com SOBRA de ${formatCurrency(result.difference)}`);
      } else {
        toast.error(`Caixa fechado com FALTA de ${formatCurrency(Math.abs(result.difference))}`);
      }
      
      setCloseCashDialog(false);
      setClosingAmount('');
      setClosingNotes('');
      await loadCurrentSession();
      await loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fechar caixa');
    }
  };

  // Create movement
  const handleCreateMovement = async () => {
    try {
      const amount = parseFloat(movementAmount.replace(',', '.'));
      if (isNaN(amount) || amount <= 0) {
        toast.error('Valor do movimento inválido');
        return;
      }
      
      await api.post(`${API_URL}/api/cash/movement`, {
        movement_type: movementType,
        amount: amount,
        notes: movementNotes || null
      });
      
      const typeLabel = movementType === 'SANGRIA' ? 'Sangria' : 'Suprimento';
      toast.success(`${typeLabel} registrada com sucesso!`);
      setMovementDialog(false);
      setMovementAmount('');
      setMovementNotes('');
      await loadCurrentSession();
      await loadMovements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar movimento');
    }
  };

  // Refresh data
  const handleRefresh = async () => {
    setLoading(true);
    await loadCurrentSession();
    await loadMovements();
    await loadHistory();
    setLoading(false);
    toast.success('Dados atualizados!');
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Carregando...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="cash-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              Caixa
            </h1>
            <p className="text-slate-600">Controle de caixa e movimentações</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            {!hasOpenSession ? (
              <Button onClick={() => setOpenCashDialog(true)} data-testid="open-cash-btn">
                <Plus className="w-4 h-4 mr-2" />
                Abrir Caixa
              </Button>
            ) : (
              <Button variant="destructive" onClick={() => setCloseCashDialog(true)} data-testid="close-cash-btn">
                <XCircle className="w-4 h-4 mr-2" />
                Fechar Caixa
              </Button>
            )}
          </div>
        </div>

        {/* Status Banner */}
        {hasOpenSession ? (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-green-800 text-lg">Caixa Aberto</p>
                  <p className="text-green-600 text-sm">
                    {currentSession?.cash_register_id} - Aberto em {formatDateTime(currentSession?.opened_at)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-green-600">Saldo Atual</p>
                  <p className="text-3xl font-bold text-green-800" data-testid="current-balance">
                    {formatCurrency(currentSession?.current_balance)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-full">
                  <AlertCircle className="w-8 h-8 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-amber-800 text-lg">Caixa Fechado</p>
                  <p className="text-amber-600 text-sm">
                    Abra o caixa para registrar vendas em dinheiro e movimentações
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <Tabs defaultValue="current" className="w-full">
          <TabsList>
            <TabsTrigger value="current" data-testid="tab-current">Sessão Atual</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Histórico</TabsTrigger>
          </TabsList>

          {/* Current Session Tab */}
          <TabsContent value="current" className="space-y-6">
            {hasOpenSession ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Wallet className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Abertura</p>
                          <p className="text-xl font-bold text-slate-900" data-testid="opening-amount">
                            {formatCurrency(currentSession?.opening_amount)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <DollarSign className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Vendas Dinheiro</p>
                          <p className="text-xl font-bold text-green-600" data-testid="cash-sales">
                            {formatCurrency(currentSession?.total_cash_sales)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <ArrowUpCircle className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Suprimentos</p>
                          <p className="text-xl font-bold text-orange-600" data-testid="suprimentos">
                            {formatCurrency(currentSession?.total_suprimentos)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <ArrowDownCircle className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Sangrias</p>
                          <p className="text-xl font-bold text-red-600" data-testid="sangrias">
                            {formatCurrency(currentSession?.total_sangrias)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Actions and Movements */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Quick Actions */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Movimentações</CardTitle>
                      <CardDescription>Registrar sangria ou suprimento</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <Button
                          variant="outline"
                          className="h-24 flex flex-col items-center justify-center gap-2 border-red-200 hover:bg-red-50 hover:border-red-300"
                          onClick={() => {
                            setMovementType('SANGRIA');
                            setMovementDialog(true);
                          }}
                          data-testid="sangria-btn"
                        >
                          <ArrowDownCircle className="w-8 h-8 text-red-500" />
                          <span className="text-red-600 font-semibold">Sangria</span>
                        </Button>
                        <Button
                          variant="outline"
                          className="h-24 flex flex-col items-center justify-center gap-2 border-green-200 hover:bg-green-50 hover:border-green-300"
                          onClick={() => {
                            setMovementType('SUPRIMENTO');
                            setMovementDialog(true);
                          }}
                          data-testid="suprimento-btn"
                        >
                          <ArrowUpCircle className="w-8 h-8 text-green-500" />
                          <span className="text-green-600 font-semibold">Suprimento</span>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recent Movements */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Movimentos do Dia</CardTitle>
                      <CardDescription>Últimas movimentações da sessão atual</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {movements.length === 0 ? (
                        <div className="text-center text-slate-500 py-8">
                          <History className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                          <p>Nenhum movimento registrado</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {movements.map((mov) => (
                            <div
                              key={mov.id}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                {mov.type === 'SANGRIA' ? (
                                  <ArrowDownCircle className="w-5 h-5 text-red-500" />
                                ) : mov.type === 'SUPRIMENTO' ? (
                                  <ArrowUpCircle className="w-5 h-5 text-green-500" />
                                ) : mov.type === 'VENDA' ? (
                                  <DollarSign className="w-5 h-5 text-emerald-500" />
                                ) : mov.type === 'ABERTURA' ? (
                                  <Wallet className="w-5 h-5 text-blue-500" />
                                ) : (
                                  <Calculator className="w-5 h-5 text-blue-500" />
                                )}
                                <div>
                                  <p className="font-medium text-slate-900">{mov.description || mov.type}</p>
                                  <p className="text-xs text-slate-500">
                                    {mov.notes || ''}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`font-bold ${
                                  mov.type === 'SANGRIA' ? 'text-red-600' 
                                  : mov.type === 'VENDA' ? 'text-emerald-600'
                                  : mov.type === 'ABERTURA' ? 'text-blue-600'
                                  : 'text-green-600'
                                }`}>
                                  {mov.type === 'SANGRIA' ? '-' : '+'}{formatCurrency(mov.amount)}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {formatDateTime(mov.created_at)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <Card className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <Wallet className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Nenhum Caixa Aberto</h3>
                  <p className="text-slate-600 mb-6">
                    Para registrar vendas em dinheiro e movimentações, primeiro abra o caixa clicando no botão acima.
                  </p>
                  <Button onClick={() => setOpenCashDialog(true)} data-testid="open-cash-empty-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Abrir Caixa Agora
                  </Button>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Caixas</CardTitle>
                <CardDescription>Últimas sessões de caixa</CardDescription>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">
                    <History className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>Nenhum histórico disponível</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="overflow-x-auto"><table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Caixa</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Abertura</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Fechamento</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Abertura</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Vendas</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Sangrias</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Suprimentos</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Saldo Final</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((session) => (
                          <tr key={session.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium">{session.cash_register_id}</td>
                            <td className="py-3 px-4 text-sm">{formatDateTime(session.opened_at)}</td>
                            <td className="py-3 px-4 text-sm">{formatDateTime(session.closed_at)}</td>
                            <td className="py-3 px-4 text-right">{formatCurrency(session.opening_amount)}</td>
                            <td className="py-3 px-4 text-right text-green-600">{formatCurrency(session.total_cash_sales)}</td>
                            <td className="py-3 px-4 text-right text-red-600">{formatCurrency(session.total_sangrias)}</td>
                            <td className="py-3 px-4 text-right text-orange-600">{formatCurrency(session.total_suprimentos)}</td>
                            <td className="py-3 px-4 text-right font-bold">{formatCurrency(session.current_balance)}</td>
                            <td className="py-3 px-4 text-center">
                              <Badge variant={session.status === 'ABERTO' ? 'default' : 'secondary'}>
                                {session.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Open Cash Dialog */}
      <Dialog open={openCashDialog} onOpenChange={setOpenCashDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abrir Caixa</DialogTitle>
            <DialogDescription>
              Informe o valor inicial e o identificador do caixa
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cash-register">Identificador do Caixa</Label>
              <Select value={cashRegisterId} onValueChange={setCashRegisterId}>
                <SelectTrigger data-testid="cash-register-select">
                  <SelectValue placeholder="Selecione o caixa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CAIXA01">CAIXA 01 - Principal</SelectItem>
                  <SelectItem value="CAIXA02">CAIXA 02 - Auxiliar</SelectItem>
                  <SelectItem value="CAIXA03">CAIXA 03 - Drive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="opening-amount">Valor de Abertura (Fundo de Troco)</Label>
              <Input
                id="opening-amount"
                type="text"
                placeholder="200.00"
                value={openingAmount}
                onChange={(e) => setOpeningAmount(e.target.value)}
                data-testid="opening-amount-input"
              />
              <p className="text-xs text-slate-500">Valor em dinheiro no momento da abertura</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCashDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleOpenCash} data-testid="confirm-open-cash-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Abrir Caixa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Cash Dialog */}
      <Dialog open={closeCashDialog} onOpenChange={setCloseCashDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fechar Caixa</DialogTitle>
            <DialogDescription>
              Informe o valor em dinheiro contado no fechamento
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {currentSession && (
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Abertura:</span>
                  <span className="font-medium">{formatCurrency(currentSession.opening_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Vendas em Dinheiro:</span>
                  <span className="font-medium text-green-600">{formatCurrency(currentSession.total_cash_sales)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Suprimentos:</span>
                  <span className="font-medium text-orange-600">{formatCurrency(currentSession.total_suprimentos)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Sangrias:</span>
                  <span className="font-medium text-red-600">-{formatCurrency(currentSession.total_sangrias)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-semibold text-slate-900">Saldo Esperado:</span>
                  <span className="font-bold text-lg text-slate-900">{formatCurrency(currentSession.current_balance)}</span>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="closing-amount">Valor Contado no Fechamento</Label>
              <Input
                id="closing-amount"
                type="text"
                placeholder="0.00"
                value={closingAmount}
                onChange={(e) => setClosingAmount(e.target.value)}
                data-testid="closing-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="closing-notes">Observações (opcional)</Label>
              <Textarea
                id="closing-notes"
                placeholder="Observações do fechamento..."
                value={closingNotes}
                onChange={(e) => setClosingNotes(e.target.value)}
                data-testid="closing-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseCashDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleCloseCash} data-testid="confirm-close-cash-btn">
              <XCircle className="w-4 h-4 mr-2" />
              Fechar Caixa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={movementDialog} onOpenChange={setMovementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {movementType === 'SANGRIA' ? 'Registrar Sangria' : 'Registrar Suprimento'}
            </DialogTitle>
            <DialogDescription>
              {movementType === 'SANGRIA'
                ? 'Retirada de dinheiro do caixa'
                : 'Entrada de dinheiro no caixa'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="movement-amount">Valor</Label>
              <Input
                id="movement-amount"
                type="text"
                placeholder="0.00"
                value={movementAmount}
                onChange={(e) => setMovementAmount(e.target.value)}
                data-testid="movement-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="movement-notes">Motivo / Observação</Label>
              <Textarea
                id="movement-notes"
                placeholder="Descreva o motivo da movimentação..."
                value={movementNotes}
                onChange={(e) => setMovementNotes(e.target.value)}
                data-testid="movement-notes-input"
              />
            </div>
            {movementType === 'SANGRIA' && currentSession && (
              <div className="text-sm text-slate-500">
                Saldo disponível: <span className="font-medium text-slate-900">{formatCurrency(currentSession.current_balance)}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovementDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateMovement}
              className={movementType === 'SANGRIA' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}
              data-testid="confirm-movement-btn"
            >
              {movementType === 'SANGRIA' ? (
                <ArrowDownCircle className="w-4 h-4 mr-2" />
              ) : (
                <ArrowUpCircle className="w-4 h-4 mr-2" />
              )}
              Confirmar {movementType === 'SANGRIA' ? 'Sangria' : 'Suprimento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}

export default CashPage;
